import { redirect } from 'react-router-dom';

// Constants
import { formOrderNaming } from '../../../../../../components/forms/NewWorkOrderForm/constants';

// GraphQL
import { client } from '../../../../../../gql/client';
import { SAVE_GENERIC_ORDER } from '../../../../../../gql/mutations/generic-order';
import { SAVE_CAMPAIGN } from '../../../../../../gql/mutations/campaign';
import {
  prependCampaignToCache,
  prependGenericOrderToCache,
} from '../../../../../../gql/cacheHelpers';
import { GET_ORDER_BY_ID } from '../../../../../../gql/queries/orders/orders';
import {
  GET_GENERIC_ORDER_BY_ID,
  GET_GENERIC_ORDERS,
} from '../../../../../../gql/queries/genericOrders';
import { GET_CAMPAIGNS } from '../../../../../../gql/queries/campaign';

// Types
import { OrderState } from '../../../../../../types/orders';
import { GenericOrCampaignInput } from './types';
import {
  tableIds,
  takeBase,
} from '../../../../../../components/display/tables/types';

// REDUX
import { setCursor } from '../../../../../../state/actions/ui/cursorTables';
import { store } from '../../../../../../state/configureStore';
import { getCursor } from '../../../../../../state/selectors/ui/tables';

//Utils
import { decompressSessionStorage } from '../../../../../../utils/sessionStorage';
import { assertDraftOwner } from '../../../../../../utils/draftOwnership';
import { ORDER_DUPLICATE_SCOPE } from '../../../../../../utils/draftScopes';
import {
  assertCopyBelongsToOwner,
  readDraftCopyId,
} from '../../../../../../utils/ownedDraftCopy';

interface DuplicateInput {
  description?: string;
  notes?: string;
  classification?: string;
  capitol?: string;
  subCapitol?: string;
  predefinedOrder?: string;
  registerInitDate?: string;
  // Autor elegido en el formulario (solo por ese camino; el guardado directo
  // no lo manda y el backend pone al que duplica).
  userId?: string;
}

export const saveData = async (id: FormDataEntryValue | null) => {
  // Barrera: guardar desde la COPIA del formulario (sin `id`; el guardado
  // directo «Ver» → «Guardar duplicado» lee del servidor y no la usa) exige que
  // la copia sea del origen que reclamó la URL. Falla CERRADA: una copia sin
  // `id`, con el de otro origen o con `id` pero sin dueño no escribe nada (el
  // paso sin origen, sin dueño y sin `id`, sigue guardando).
  if (!id) {
    const copyId = readDraftCopyId(formOrderNaming.NEW_DUPLICATE_WORK_ORDER_FORM);
    assertDraftOwner(ORDER_DUPLICATE_SCOPE, copyId, { isReplay: false });
    assertCopyBelongsToOwner(
      ORDER_DUPLICATE_SCOPE,
      formOrderNaming.NEW_DUPLICATE_WORK_ORDER_FORM
    );
  }

  let orderInput: DuplicateInput = {};
  let genericOrCampaignInput: GenericOrCampaignInput = {};
  let isCampaignOrGeneric = false;
  let campaign: string | undefined;

  const dispatch = store.dispatch;
  const cursorWorkOrderGeneralView = getCursor(tableIds.WORKORDER_GENERAL_VIEW)(store.getState());
  const cursorDuplicateGenericOrder = getCursor(tableIds.DUPLICATE_GENERIC_ORDER)(store.getState());
  const cursorSearchAndCreateWorkOrders = getCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS)(store.getState());
  const cursorGenericOrderModal = getCursor(tableIds.GENERIC_ORDER_MODAL)(store.getState());
  const creationDate = new Date().toISOString();
  const cursorCampaigns = getCursor(tableIds.CAMPAIGN_MODAL)(store.getState());
  const cursorCampaignModal = getCursor(tableIds.CAMPAIGNS)(store.getState());

  if (id) {
    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id },
    });

    // Guardado directo desde el resumen («Ver» → «Guardar duplicado»): el
    // duplicado es un registro NUEVO de quien duplica, así que su fecha de
    // registro es el instante en que se duplica — no la de la original.
    orderInput = {
      registerInitDate: creationDate,
      description: order.description,
      notes: order.notes,
      classification: order.classificationIDs[0],
      capitol: order.capitolIDs[0],
      subCapitol: order.subCapitolIDs[0],
      ...(order.predefinedOrderIDs?.[0] && { predefinedOrder: order.predefinedOrderIDs[0] }),
    };

    const {
      data: { genericOrder },
    } = await client.query({
      query: GET_GENERIC_ORDER_BY_ID,
      variables: { id: order.genericOrdersIDs[0] },
    });

    genericOrCampaignInput = {
      concessionId: genericOrder.concessionIDs[0],
      roadId: genericOrder.roadIDs[0],
      teamIds: genericOrder.teamIDs ?? [],
      track: genericOrder.track,
      lane: genericOrder.lane,
      creationDate,
      registerInitDate: creationDate,
      direction: genericOrder?.direction,
      margin: genericOrder?.margin,
      pkInitKm: genericOrder.pkInitKm,
      pkInitMeter: genericOrder.pkInitMeter,
      pkEndKm: genericOrder.pkEndKm,
      pkEndMeter: genericOrder.pkEndMeter,
    };
  } else {
    const dataFormRaw = sessionStorage.getItem(
      formOrderNaming.NEW_DUPLICATE_WORK_ORDER_FORM
    );
    if (dataFormRaw) {
      const dataForm = JSON.parse(decompressSessionStorage(dataFormRaw));
      isCampaignOrGeneric = dataForm.isCampaignActive === 'true';
      campaign = dataForm.campaign;

      orderInput = {
        registerInitDate: dataForm.registerInitDate,
        userId: dataForm.userId,
        description: dataForm.description,
        notes: dataForm.notes,
        classification: dataForm.classification,
        capitol: dataForm.capitol,
        subCapitol: dataForm.subCapitol,
        ...(dataForm.predefine && { predefinedOrder: dataForm.predefine }),
      };

      const pkInitRaw = dataForm.pkInit.replace(/ /g, '').split('+');
      const pkEndRaw = dataForm.pkEnd.replace(/ /g, '').split('+');

      genericOrCampaignInput = {
        concessionId: dataForm.concession,
        roadId: dataForm.road,
        teamIds: Array.isArray(dataForm.teamRelationships)
          ? dataForm.teamRelationships
          : dataForm.teamRelationships
            ? String(dataForm.teamRelationships).split(',').map((s: string) => s.trim()).filter(Boolean)
            : [],
        track: dataForm.track,
        lane: dataForm.lane,
        creationDate,
        registerInitDate: dataForm.registerInitDate,
        direction: dataForm?.direction,
        margin: dataForm?.margin,
        pkInitKm: Number(pkInitRaw[0]),
        pkInitMeter: Number(pkInitRaw[1]),
        pkEndKm: Number(pkEndRaw[0]),
        pkEndMeter: Number(pkEndRaw[1]),
      };
    }
  }

  // Bloque C-5.5: SAVE_CAMPAIGN / SAVE_GENERIC_ORDER atómicos sustituyen
  // CREATE_NEW_ORDER + CREATE_NEW_GENERIC_ORDER/CAMPAIGN + UPDATE_ORDER +
  // UPDATE_ORDER_RELATIONSHIPS por una sola transacción Prisma.
  let createdOrder;

  if (isCampaignOrGeneric) {
    const result = await client.mutate({
      mutation: SAVE_CAMPAIGN,
      variables: {
        input: {
          // Idempotencia online: UUID estable por operación (RetryLink no duplica).
          clientOpId: crypto.randomUUID(),
          state: OrderState.Open,
          creationDate,
          registerInitDate: orderInput.registerInitDate,
          // El backend lo valida (usuario app activo del tenant) y, si no
          // vale, pone al autenticado: nunca rechaza.
          ...(orderInput.userId && { userId: orderInput.userId }),
          pkInitKm: genericOrCampaignInput.pkInitKm,
          pkInitMeter: genericOrCampaignInput.pkInitMeter,
          pkEndKm: genericOrCampaignInput.pkEndKm,
          pkEndMeter: genericOrCampaignInput.pkEndMeter,
          track: genericOrCampaignInput.track,
          direction: genericOrCampaignInput.direction,
          margin: genericOrCampaignInput.margin,
          lane: genericOrCampaignInput.lane,
          concessionId: genericOrCampaignInput.concessionId,
          roadId: genericOrCampaignInput.roadId,
          teamIds: genericOrCampaignInput.teamIds ?? [],
          capitolId: orderInput.capitol,
          subCapitolId: orderInput.subCapitol,
          classification: orderInput.classification,
          ...(orderInput.predefinedOrder && { predefinedOrderId: orderInput.predefinedOrder }),
          description: orderInput.description,
          notes: orderInput.notes,
          name: campaign,
        },
      },
      update(cache, { data: { saveCampaign } }) {
        prependCampaignToCache(cache, saveCampaign.campaign);
        if (cursorCampaigns?.cursor !== undefined) {
          dispatch(setCursor(tableIds.CAMPAIGNS, {
            ...cursorCampaigns, take: takeBase, cursor: '', orderBy: 'desc',
          }));
          dispatch(setCursor(tableIds.CAMPAIGN_MODAL, {
            ...cursorCampaignModal, take: takeBase, cursor: '', orderBy: 'desc',
          }));
        }
      },
    });
    createdOrder = result.data?.saveCampaign?.order;
  } else {
    const result = await client.mutate({
      mutation: SAVE_GENERIC_ORDER,
      variables: {
        input: {
          // Idempotencia online: UUID estable por operación (RetryLink no duplica).
          clientOpId: crypto.randomUUID(),
          state: OrderState.Open,
          creationDate,
          registerInitDate: orderInput.registerInitDate,
          // El backend lo valida (usuario app activo del tenant) y, si no
          // vale, pone al autenticado: nunca rechaza.
          ...(orderInput.userId && { userId: orderInput.userId }),
          pkInitKm: genericOrCampaignInput.pkInitKm,
          pkInitMeter: genericOrCampaignInput.pkInitMeter,
          pkEndKm: genericOrCampaignInput.pkEndKm,
          pkEndMeter: genericOrCampaignInput.pkEndMeter,
          track: genericOrCampaignInput.track,
          direction: genericOrCampaignInput.direction,
          margin: genericOrCampaignInput.margin,
          lane: genericOrCampaignInput.lane,
          concessionId: genericOrCampaignInput.concessionId,
          roadId: genericOrCampaignInput.roadId,
          teamIds: genericOrCampaignInput.teamIds ?? [],
          capitolId: orderInput.capitol,
          subCapitolId: orderInput.subCapitol,
          classification: orderInput.classification,
          ...(orderInput.predefinedOrder && { predefinedOrderId: orderInput.predefinedOrder }),
          description: orderInput.description,
          notes: orderInput.notes,
        },
      },
      update(cache, { data: { saveGenericOrder } }) {
        prependGenericOrderToCache(cache, saveGenericOrder.genericOrder);
        if (cursorWorkOrderGeneralView?.cursor !== undefined) {
          dispatch(setCursor(tableIds.WORKORDER_GENERAL_VIEW, {
            ...cursorWorkOrderGeneralView, take: takeBase, cursor: '', orderBy: 'desc',
          }));
          dispatch(setCursor(tableIds.DUPLICATE_GENERIC_ORDER, {
            ...cursorDuplicateGenericOrder, take: takeBase, cursor: '', orderBy: 'desc',
          }));
          dispatch(setCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS, {
            ...cursorSearchAndCreateWorkOrders, take: takeBase, cursor: '', orderBy: 'desc',
          }));
          dispatch(setCursor(tableIds.GENERIC_ORDER_MODAL, {
            ...cursorGenericOrderModal, take: takeBase, cursor: '', orderBy: 'desc',
          }));
        }
      },
    });
    createdOrder = result.data?.saveGenericOrder?.order;
  }

  // Refetch the new order and invalidate the duplicate list cache
  if (createdOrder?.id) {
    const refetchPromises: Promise<unknown>[] = [
      client.query({
        fetchPolicy: 'network-only',
        query: GET_ORDER_BY_ID,
        variables: { id: createdOrder.id },
      }),
    ];

    if (isCampaignOrGeneric) {
      refetchPromises.push(
        client.query({
          fetchPolicy: 'network-only',
          query: GET_CAMPAIGNS,
          variables: {
            input: {
              take: cursorCampaigns.take,
              cursor: '',
              orderBy: 'desc',
              concessionId: cursorCampaigns.concessionId,
              fromDate: cursorCampaigns.fromDate,
              toDate: cursorCampaigns.toDate,
              roadId: cursorCampaigns.roadId,
            },
          },
        })
      );
    } else {
      refetchPromises.push(
        client.query({
          fetchPolicy: 'network-only',
          query: GET_GENERIC_ORDERS,
          variables: {
            input: {
              take: cursorDuplicateGenericOrder.take,
              cursor: '',
              orderBy: 'desc',
              concessionId: cursorDuplicateGenericOrder.concessionId,
              fromDate: cursorDuplicateGenericOrder.fromDate,
              toDate: cursorDuplicateGenericOrder.toDate,
              indexType: cursorDuplicateGenericOrder.indexType,
              indexExpedition: cursorDuplicateGenericOrder.indexExpedition,
              capitolId: cursorDuplicateGenericOrder.capitolId,
              subCapitolId: cursorDuplicateGenericOrder.subCapitolId,
              classification: cursorDuplicateGenericOrder?.classification,
              state: cursorDuplicateGenericOrder?.state,
            },
          },
        })
      );
    }

    await Promise.all(refetchPromises);
  }

  // Clean the form local storage as it was just saved
  sessionStorage.removeItem(formOrderNaming.NEW_DUPLICATE_WORK_ORDER_FORM);
  sessionStorage.removeItem(ORDER_DUPLICATE_SCOPE.ownerKey);
  return isCampaignOrGeneric
    ? redirect(`../../campaign/${createdOrder?.id ?? ''}`)
    : redirect(`../../${createdOrder?.id ?? ''}`);
};
